import { makeAutoObservable, reaction } from 'mobx';
import { ChangeEvent } from 'react';

import {
  distanceOptions,
  maxEnlargment,
  maxEnlargmentKm,
  maxEnlargmentPercent,
  maxRouteLength,
  minLength,
  minRouteLength,
} from 'constants/routeParams';
import { IntegerField } from 'store/fileds/IntegerField';
import { MinMaxField } from 'store/fileds/MinMaxField';
import { SelectField } from 'store/fileds/SelectField';

import { RootStore } from '../../RootStore';

class RouteParams {
  root;
  routeLength;
  maxEnlargmentUnit;
  maxEnlargment;
  minLength;
  minLengthUnit;
  isRouteLengthActive = false;
  isEllipseActive = false;
  onRouteActive;
  onEllipseActive;

  constructor(root: RootStore) {
    this.root = root;
    this.routeLength = new MinMaxField(root, {
      default: { min: minRouteLength.toString(), max: maxRouteLength.toString() },
    });

    this.maxEnlargment = new IntegerField({ default: maxEnlargment.toString() });
    this.maxEnlargmentUnit = new SelectField(this.root.options.getEllipseDistance);

    this.minLength = new IntegerField({ default: minLength.toString() });
    this.minLengthUnit = new SelectField(this.root.options.getEllipseDistance);

    makeAutoObservable(this);

    this.onRouteActive = reaction(
      () => this.isRouteLengthActive,
      () => {
        if (this.root.app.isDataSetting) return;

        if (this.isRouteLengthActive) {
          this.setInitialRouteLength();
        }
      },
    );

    this.onEllipseActive = reaction(
      () => this.isEllipseActive,
      () => {
        if (this.root.app.isDataSetting) return;

        if (this.isEllipseActive) {
          this.setInitialEllipse();

          const { from, to } = this.root.filter;

          [from, to].forEach(geo => {
            if (!geo.isCityLocality) {
              geo.resetList();
              geo.locality.clear();
            }
          });
        }
      },
    );
  }

  get isDefault() {
    return (
      this.routeLength.isDefault &&
      this.maxEnlargment.isDefault &&
      this.maxEnlargmentUnit.isDefault &&
      this.minLength.isDefault &&
      this.minLengthUnit.isDefault
    );
  }

  get ellipseData(): TEllipse {
    return {
      maxEnlargment: this.maxEnlargment.data,
      maxEnlargmentUnit: this.maxEnlargmentUnit.data,
      minLength: this.minLength.data,
      minLengthUnit: this.minLengthUnit.data,
    };
  }

  get isRouteParamActive() {
    return this.isEllipseActive || this.isRouteLengthActive;
  }

  get requestData() {
    if (this.isRouteLengthActive) {
      return {
        routeLength: this.routeLength.requestData,
      };
    }

    if (this.isEllipseActive) {
      return {
        ellipse: this.ellipseData,
      };
    }

    return null;
  }

  setIsEllipseActive = (value: boolean) => {
    this.isRouteLengthActive = false;
    this.isEllipseActive = value;
  };

  setIsRouteLengthActive = (value: boolean) => {
    this.isEllipseActive = false;
    this.isRouteLengthActive = value;
  };

  setInitialEllipse = () => {
    this.maxEnlargment.setValue(maxEnlargment);
    this.minLength.setValue(minLength);
  };

  normalizeMaxEnlargmentByUnit = () => {
    const unit = this.maxEnlargmentUnit.data;
    let limit: number | null = null;
    if (unit === distanceOptions.percent) limit = maxEnlargmentPercent;
    else if (unit === distanceOptions.km) limit = maxEnlargmentKm;
    else return;

    const data = this.maxEnlargment.data;
    if (data == null || data <= limit) return;

    this.maxEnlargment.setValue(limit);
  };

  setMaxEnlargmentFromInput = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    this.maxEnlargment.setValueFromInput(event);
    this.normalizeMaxEnlargmentByUnit();
  };

  setMaxEnlargmentUnitOption = (option: ReturnType<RootStore['options']['getEllipseDistance']>[number]) => {
    this.maxEnlargmentUnit.setOption(option);
    this.normalizeMaxEnlargmentByUnit();
  };

  setInitialRouteLength = () => {
    this.routeLength.min.setValue(minRouteLength);
    this.routeLength.max.setValue(maxRouteLength);
  };
}

export { RouteParams };
